// frontend/src/app/components/truth-set/truth-set-panel.tsx
//
// The TRUTH SET (truth set, part 3; backend app/services/truth_set/
// truth_service.py): what is true about the brand, decided by a person and
// confirmed as a numbered version. Used in two places:
//   * Stage 3 (admin): every fact, internal conflicts first, plus the record
//     of why a brand goes without a truth set when it does.
//   * Deep Signal, Facts tab (client): the same list without the facts that
//     are in conflict on the brand's own site (the admin settles those first).
// The server decides the role from the viewer.
//
// Each fact can be confirmed (the site's value is right), corrected (a
// person's value replaces it), removed, or reset to proposed; a fact the site
// does not state can be added. Decisions are a draft until someone confirms
// the set: that locks a version with who and when, and only versions are used
// to check AI answers.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, Check, ChevronDown, ChevronUp, History, Loader2, Pencil, Plus, RotateCcw, ShieldCheck, X,
} from "lucide-react";
import { toast } from "sonner";

import {
  addManualFact, confirmTruthSet, decideFact, getTruthSet, getTruthSetVersion, setOnboardingSkip,
  type TruthFact, type TruthSetVersion, type TruthSetView,
} from "@/api/truth-set";

const ACCENT = "var(--spectral-violet)";
const WARN = "var(--infrared-coral)";
const OK = "var(--signal-green, green)";

const box: React.CSSProperties = {
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 8,
};
const label: React.CSSProperties = {
  display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
  letterSpacing: "0.04em", color: "var(--muted-foreground)", marginBottom: 4,
};
const input: React.CSSProperties = {
  width: "100%", padding: "6px 10px", fontSize: "var(--text-sm)", color: "var(--foreground)",
  backgroundColor: "var(--background)", border: "1px solid var(--border)", borderRadius: 6, outline: "none",
};
const muted: React.CSSProperties = { color: "var(--muted-foreground)", fontSize: "var(--text-xs)" };
const btn = (primary = false, disabled = false): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 6,
  fontSize: "var(--text-xs)", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.55 : 1,
  border: primary ? "none" : "1px solid var(--border)",
  backgroundColor: primary ? ACCENT : "transparent",
  color: primary ? "white" : "var(--foreground)",
});
const small = (disabled = false): React.CSSProperties => ({ ...btn(false, disabled), padding: "2px 8px" });

const STATUS_LABEL: Record<TruthFact["status"], string> = {
  proposed: "Proposed", confirmed: "Confirmed", corrected: "Corrected", removed: "Removed",
};
const STATUS_COLOR: Record<TruthFact["status"], string> = {
  proposed: "var(--muted-foreground)", confirmed: OK, corrected: ACCENT, removed: WARN,
};

function day(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "";
}

function factName(f: { attribute_label?: string; attribute: string; subject_type: string; subject_name: string; qualifier: string }) {
  let name = f.attribute_label || f.attribute;
  if (f.subject_type !== "brand") name += ` (${f.subject_name})`;
  if (f.qualifier) name += `, ${f.qualifier}`;
  return name;
}

export function TruthSetPanel({ customerId, embedded = false }: { customerId: number; embedded?: boolean }) {
  const [view, setView] = useState<TruthSetView | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState<number | null>(null);
  const [draftValue, setDraftValue] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [newFact, setNewFact] = useState({ attribute: "", value: "", subject_type: "brand", subject: "", qualifier: "" });
  const [note, setNote] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [openVersion, setOpenVersion] = useState<TruthSetVersion | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setView(await getTruthSet(customerId));
    } catch (e: any) {
      toast.error(e?.message || "Could not load the truth set.");
    }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const act = async (fn: () => Promise<TruthSetView | unknown>, done?: string) => {
    setBusy(true);
    try {
      const out = await fn();
      if (out && typeof out === "object" && "available" in (out as TruthSetView)) setView(out as TruthSetView);
      else await refresh();
      if (done) toast.success(done);
    } catch (e: any) {
      toast.error(e?.message || "That did not work.");
    } finally {
      setBusy(false);
    }
  };

  const facts = view?.facts || [];
  const conflicts = useMemo(() => facts.filter((f) => f.in_conflict && f.status === "proposed"), [facts]);
  const undecided = useMemo(() => facts.filter((f) => f.status === "proposed" && f.active && !f.in_conflict), [facts]);
  const decided = useMemo(() => facts.filter((f) => f.status !== "proposed" && (showRemoved || f.status !== "removed")),
    [facts, showRemoved]);

  if (!view) {
    return <div style={{ ...box, padding: 16, marginBottom: 16 }}><span style={muted}><Loader2 className="inline w-3 h-3 animate-spin" /> Loading the truth set…</span></div>;
  }
  if (!view.available) {
    return (
      <div style={{ ...box, padding: 16, marginBottom: 16 }}>
        <span style={muted}>The truth set is not available yet: the truth-set migrations have not been applied.</span>
      </div>
    );
  }

  const isAdmin = view.role === "admin";
  const latest = view.latest_version;
  const counts = view.counts!;

  const decide = (fact: TruthFact, action: "confirm" | "correct" | "remove" | "reset", value?: string) =>
    act(() => decideFact(customerId, fact.id, { action, value }));

  const row = (fact: TruthFact) => {
    const shown = fact.status === "corrected" || fact.origin === "manual" ? fact.truth_value || fact.value : fact.value;
    return (
      <div key={fact.id} style={{ borderTop: "1px solid var(--border)", padding: "8px 0" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ minWidth: 160, fontSize: "var(--text-xs)", fontWeight: 600 }}>{factName(fact)}</span>
          {editing === fact.id ? (
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flex: 1 }}>
              <input style={{ ...input, maxWidth: 260 }} value={draftValue} autoFocus
                     onChange={(e) => setDraftValue(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter" && draftValue.trim()) { setEditing(null); decide(fact, "correct", draftValue); } }} />
              <button type="button" style={small(busy || !draftValue.trim())} disabled={busy || !draftValue.trim()}
                      onClick={() => { setEditing(null); decide(fact, "correct", draftValue); }}>Save</button>
              <button type="button" style={small()} onClick={() => setEditing(null)}><X className="w-3 h-3" /></button>
            </span>
          ) : (
            <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, textDecoration: fact.status === "removed" ? "line-through" : undefined }}>
              {shown}
            </span>
          )}
          {fact.status === "corrected" && <span style={muted}>site says {fact.value}</span>}
          <span style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: STATUS_COLOR[fact.status] }}>
            {STATUS_LABEL[fact.status]}{fact.origin === "manual" ? " · added by hand" : ""}
          </span>
          {fact.status !== "proposed" && fact.origin === "site" && !fact.active && (
            <span style={{ ...muted, color: WARN }}>no longer on the site</span>
          )}
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4 }}>
            {fact.status !== "confirmed" && fact.origin === "site" && fact.status !== "corrected" && (
              <button type="button" style={small(busy)} disabled={busy} title="The site's value is right"
                      onClick={() => decide(fact, "confirm")}><Check className="w-3 h-3" /> Confirm</button>
            )}
            {fact.status !== "removed" && (
              <button type="button" style={small(busy)} disabled={busy} title="Give the right value"
                      onClick={() => { setEditing(fact.id); setDraftValue(fact.truth_value || fact.value); }}>
                <Pencil className="w-3 h-3" /> Correct
              </button>
            )}
            {fact.status !== "removed" && (
              <button type="button" style={small(busy)} disabled={busy} title="Not true, or not worth holding"
                      onClick={() => decide(fact, "remove")}><X className="w-3 h-3" /> Remove</button>
            )}
            {fact.status !== "proposed" && fact.origin === "site" && (
              <button type="button" style={small(busy)} disabled={busy} title="Undo the decision"
                      onClick={() => decide(fact, "reset")}><RotateCcw className="w-3 h-3" /></button>
            )}
            {fact.sources.length > 0 && (
              <button type="button" style={small()} onClick={() => setExpanded(expanded === fact.id ? null : fact.id)}>
                {fact.source_count} source{fact.source_count === 1 ? "" : "s"}
              </button>
            )}
          </span>
        </div>
        {fact.decided_at && fact.status !== "proposed" && (
          <div style={{ ...muted, marginTop: 2 }}>
            {fact.decided_role === "client" ? "Decided by the client" : "Decided by Upmode"}
            {isAdmin && fact.decided_by ? ` (${fact.decided_by})` : ""} on {day(fact.decided_at)}
            {fact.decision_note ? ` · ${fact.decision_note}` : ""}
          </div>
        )}
        {expanded === fact.id && (
          <div style={{ marginTop: 6, paddingLeft: 12, display: "flex", flexDirection: "column", gap: 6 }}>
            {fact.sources.map((s, i) => (
              <div key={i} style={{ fontSize: "var(--text-xs)" }}>
                <div style={{ ...muted, wordBreak: "break-all" }}>{s.source_label}{s.seen_at ? ` · seen ${day(s.seen_at)}` : ""}</div>
                <div style={{ fontStyle: "italic" }}>“{s.sentence}”</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ ...box, padding: 16, marginBottom: 16 }}>
      {!embedded && (
        <button type="button" onClick={() => setOpen(!open)}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none",
                         cursor: "pointer", padding: 0, color: "var(--foreground)", marginBottom: open ? 12 : 0 }}>
          <ShieldCheck className="w-4 h-4" style={{ color: ACCENT }} />
          <span style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>Truth set</span>
          <span style={{ ...muted, marginLeft: 8 }}>
            {latest ? `v${latest.version_number} confirmed ${day(latest.confirmed_at)}` : "not confirmed yet"}
            {counts.proposed ? ` · ${counts.proposed} to decide` : ""}
          </span>
          <span style={{ marginLeft: "auto" }}>{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
        </button>
      )}

      {(open || embedded) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ ...muted, lineHeight: 1.5 }}>
            What is true about {view.brand || "the brand"}. Facts are proposed from the brand's own site and documents;
            nothing is true here until a person confirms it, and only a confirmed version is used to check what AI
            answers say.
          </p>

          {/* Status and confirm */}
          <div style={{ ...box, padding: 12, borderColor: view.can_confirm ? ACCENT : "var(--border)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
                {latest ? `Version ${latest.version_number}` : "No confirmed version yet"}
              </span>
              {latest && (
                <span style={muted}>
                  {latest.fact_count} facts · confirmed {day(latest.confirmed_at)} by
                  {latest.confirmed_role === "client" ? " the client" : " Upmode"}{isAdmin ? ` (${latest.confirmed_by})` : ""}
                </span>
              )}
              <button type="button" style={{ ...btn(true, busy || !view.can_confirm), marginLeft: "auto" }}
                      disabled={busy || !view.can_confirm} onClick={() => setReviewing(!reviewing)}>
                <ShieldCheck className="w-3 h-3" /> {latest ? `Confirm version ${latest.version_number + 1}` : "Confirm the truth set"}
              </button>
            </div>
            {(view.blockers || []).length > 0 && (
              <ul style={{ ...muted, paddingLeft: 16, listStyle: "disc", marginTop: 6 }}>
                {view.blockers!.map((b) => <li key={b}>{b}</li>)}
              </ul>
            )}
            {!view.blockers?.length && latest && !view.pending_lines?.length && (
              <p style={{ ...muted, marginTop: 6 }}>No changes since version {latest.version_number}.</p>
            )}
            {reviewing && view.can_confirm && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={label}>{latest ? `What changes from version ${latest.version_number}` : "What the first version holds"}</span>
                <ul style={{ fontSize: "var(--text-xs)", paddingLeft: 16, listStyle: "disc" }}>
                  {(view.pending_lines || []).map((line) => <li key={line}>{line}</li>)}
                </ul>
                <input style={input} placeholder="Note (optional): why it changed" value={note}
                       onChange={(e) => setNote(e.target.value)} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" style={btn(true, busy)} disabled={busy}
                          onClick={() => act(async () => {
                            await confirmTruthSet(customerId, { note, expected_version_id: latest?.id ?? null });
                            setReviewing(false);
                            setNote("");
                          }, "Truth set confirmed")}>
                    Confirm and lock this version
                  </button>
                  <button type="button" style={btn()} onClick={() => setReviewing(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* Admin: going without a truth set */}
          {isAdmin && !latest && (
            <div style={{ ...muted }}>
              {view.onboarding_skip ? (
                <span>
                  Onboarding goes ahead without a truth set: “{view.onboarding_skip.skip_reason}” ({view.onboarding_skip.skipped_by},
                  {" "}{day(view.onboarding_skip.skipped_at)}).{" "}
                  <button type="button" style={small(busy)} disabled={busy}
                          onClick={() => act(() => setOnboardingSkip(customerId, null), "Requirement restored")}>Undo</button>
                </span>
              ) : (
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  The framework's first approval needs a confirmed truth set. If this brand goes without one, say why:
                  <input style={{ ...input, width: 260 }} placeholder="e.g. no public website yet" value={skipReason}
                         onChange={(e) => setSkipReason(e.target.value)} />
                  <button type="button" style={small(busy || !skipReason.trim())} disabled={busy || !skipReason.trim()}
                          onClick={() => act(() => setOnboardingSkip(customerId, skipReason), "Recorded")}>Record</button>
                </span>
              )}
            </div>
          )}

          {/* Conflicts first (admin only) */}
          {conflicts.length > 0 && (
            <div style={{ ...box, padding: 12, borderColor: WARN }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <AlertTriangle className="w-4 h-4" style={{ color: WARN }} />
                <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>The brand's own site disagrees with itself</span>
              </div>
              <p style={{ ...muted, marginBottom: 4 }}>
                Keep the right value and remove the others (or correct one) before the set can be confirmed.
              </p>
              {conflicts.map(row)}
            </div>
          )}

          {undecided.length > 0 && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={label}>To decide ({undecided.length})</span>
                <button type="button" style={{ ...small(busy), marginLeft: "auto" }} disabled={busy}
                        title="Confirm every fact listed here as the site states it"
                        onClick={() => act(async () => {
                          for (const fact of undecided) await decideFact(customerId, fact.id, { action: "confirm" });
                        }, `${undecided.length} facts confirmed`)}>
                  <Check className="w-3 h-3" /> Confirm all {undecided.length} as stated
                </button>
              </div>
              {undecided.map(row)}
            </div>
          )}

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={label}>Decided ({decided.length})</span>
              <label style={{ ...muted, marginLeft: "auto", display: "flex", gap: 4, alignItems: "center" }}>
                <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} />
                show removed
              </label>
              <button type="button" style={small()} onClick={() => setAdding(!adding)}><Plus className="w-3 h-3" /> Add a fact</button>
            </div>
            {adding && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, margin: "8px 0" }}>
                <select style={input} value={newFact.attribute} onChange={(e) => setNewFact({ ...newFact, attribute: e.target.value })}>
                  <option value="">Attribute…</option>
                  {(view.vocabulary || []).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                </select>
                <select style={input} value={newFact.subject_type} onChange={(e) => setNewFact({ ...newFact, subject_type: e.target.value })}>
                  <option value="brand">About the brand</option>
                  <option value="product">About a product</option>
                  <option value="person">About a person</option>
                  <option value="location">About a location</option>
                </select>
                {newFact.subject_type !== "brand" && (
                  <input style={input} placeholder="Which one" value={newFact.subject}
                         onChange={(e) => setNewFact({ ...newFact, subject: e.target.value })} />
                )}
                <input style={input} placeholder="Value" value={newFact.value}
                       onChange={(e) => setNewFact({ ...newFact, value: e.target.value })} />
                <input style={input} placeholder="Applies to (optional)" value={newFact.qualifier}
                       onChange={(e) => setNewFact({ ...newFact, qualifier: e.target.value })} />
                <div>
                  <button type="button" style={btn(true, busy || !newFact.attribute || !newFact.value.trim())}
                          disabled={busy || !newFact.attribute || !newFact.value.trim()}
                          onClick={() => act(async () => {
                            const out = await addManualFact(customerId, newFact);
                            setAdding(false);
                            setNewFact({ attribute: "", value: "", subject_type: "brand", subject: "", qualifier: "" });
                            return out;
                          }, "Fact added")}>
                    Add
                  </button>
                </div>
              </div>
            )}
            {decided.length ? decided.map(row) : <p style={muted}>Nothing decided yet.</p>}
          </div>

          {/* History */}
          {(view.versions || []).length > 0 && (
            <div>
              <span style={label}><History className="inline w-3 h-3" /> Versions</span>
              {view.versions!.map((v) => (
                <div key={v.id} style={{ borderTop: "1px solid var(--border)", padding: "6px 0", fontSize: "var(--text-xs)" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "baseline", cursor: "pointer" }}
                       onClick={async () => {
                         if (openVersion?.id === v.id) { setOpenVersion(null); return; }
                         try { setOpenVersion(await getTruthSetVersion(customerId, v.id)); }
                         catch (e: any) { toast.error(e?.message || "Could not load that version."); }
                       }}>
                    <span style={{ fontWeight: 600 }}>Version {v.version_number}</span>
                    <span style={muted}>
                      {day(v.confirmed_at)} · {v.confirmed_role === "client" ? "the client" : "Upmode"}
                      {isAdmin ? ` (${v.confirmed_by})` : ""} · {v.fact_count} facts
                      {v.note ? ` · ${v.note}` : ""}
                    </span>
                  </div>
                  {v.lines.length > 0 && (
                    <ul style={{ ...muted, paddingLeft: 16, listStyle: "disc" }}>
                      {v.lines.slice(0, 8).map((line) => <li key={line}>{line}</li>)}
                      {v.lines.length > 8 && <li>and {v.lines.length - 8} more</li>}
                    </ul>
                  )}
                  {openVersion?.id === v.id && (
                    <div style={{ ...box, padding: 8, marginTop: 4 }}>
                      {(openVersion.facts || []).map((f, i) => (
                        <div key={i} style={{ display: "flex", gap: 8 }}>
                          <span style={{ minWidth: 160, fontWeight: 600 }}>{factName({ ...f, attribute: f.attribute_label })}</span>
                          <span>{f.value}</span>
                          <span style={muted}>{f.status}{f.origin === "manual" ? " · added by hand" : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
